import React from 'react';
import { Document } from '@contentful/rich-text-types';

import { IPlan, IPlansTableFields } from '../../../types/contentful';

import { CFColorToken } from '../../theme/tokens/contentful-color';

import { Plan } from '../Plan';
import RichText from '../RichText';
import { Text } from '../Text';
import { Box } from '../Box';
import { BlockContainer, Layout, BoxColumn } from '../Layout';
import PlansIcon from './PlansIcon';

import {
  MobileVersion,
  DesktopVersion,
  Table,
  TableTitle,
  TableHead,
  PlanText,
  FeatureRow,
  BufferCell,
  FeatureCell,
  Checkmark
} from './styles';

interface Feature {
  includedInNumberOfPlans: number;
  title?: string | null;
  info?: Document | null;
  replaceCorePlanCheckWith?: string | null;
}

export type Features = Feature[];

export function PlansTable({
  title,
  desktopTitle,
  plans = []
}: IPlansTableFields) {
  if (!plans) {
    return <div />;
  }

  const allFeatures: Features = plans.reduce(
    (acc: Features, plan, index: number) => {
      const { fields } = (plan as IPlan) ?? {};

      if (!fields || !fields.features) {
        return acc;
      }
      const features: Features = fields.features.map(
        ({ fields: featureFields }) => ({
          ...featureFields,
          includedInNumberOfPlans: index + 1
        })
      );
      acc = acc.concat(features);
      return acc;
    },
    []
  );

  return (
    <BlockContainer
      marginTop={{ 0: 'blockMarginY', tablet: 'blockPadding_tablet' }}
      marginBottom={{ 0: 'blockMarginY', tablet: 'blockMarginY_tablet' }}
      data-blockid="plansTable"
    >
      <Layout>
        <BoxColumn
          size={{ 0: 12, desktop: 10 }}
          paddingTop={{ 0: 0, desktop: 'xs' }}
        >
          <MobileVersion
            data-blockid="plans-table-mobile"
            data-testid="plans-table-mobile"
          >
            <Text
              as="h2"
              fontWeight={{ 0: 'h2' }}
              fontSize={{ 0: 'h2_0', desktop: 'h2_desktop' }}
              data-blockid="plansTable-desktop-title"
              paddingLeft="m"
            >
              {title}
            </Text>
            {(plans || []).map(({ sys, fields }, idx: number) => (
              <Plan
                {...fields}
                key={sys?.id}
                features={allFeatures.filter(
                  (feature) => feature.includedInNumberOfPlans <= idx + 1
                )}
              />
            ))}
          </MobileVersion>
          <DesktopVersion
            data-blockid="plans-table-desktop"
            data-testid="plans-table-desktop"
          >
            <Table>
              <thead>
                <tr>
                  <TableTitle>
                    <Text
                      as="h2"
                      fontWeight={{ 0: 'h2' }}
                      fontSize={{ 0: 'h2_0', desktop: 'h2_desktop' }}
                      data-blockid="plansTable-desktop-title"
                      textAlign="left"
                    >
                      {desktopTitle}
                    </Text>
                  </TableTitle>
                  <th></th>
                  {(plans || []).map(({ sys, fields }) => (
                    <React.Fragment key={sys?.id}>
                      <th></th>
                      <TableHead key={sys?.id} color={fields?.color ?? ''}>
                        <PlanText
                          as="h4"
                          fontWeight="h4"
                          fontSize="h3_tablet"
                          data-blockid="plansTable-desktop-title"
                          color={fields?.textColor ?? ('black' as CFColorToken)}
                        >
                          {fields?.name}
                        </PlanText>
                        <PlanText
                          as="p"
                          fontSize="m"
                          fontWeight="h3"
                          data-blockid="plansTable-desktop-info"
                          color={fields?.textColor ?? ('black' as CFColorToken)}
                          paddingTop="sm"
                        >
                          {fields?.subheader}
                        </PlanText>
                      </TableHead>
                    </React.Fragment>
                  ))}
                </tr>
              </thead>
              <tbody>
                {allFeatures.map((feature, index: number) => (
                  <FeatureRow key={index}>
                    <FeatureCell>
                      <Text as="h3" fontWeight="h3" fontSize="h3_tablet">
                        {feature && feature.title}
                      </Text>
                      <Box paddingTop="sm">
                        {feature?.info && <RichText data={feature.info} />}
                      </Box>
                    </FeatureCell>
                    <BufferCell />
                    {plans?.map(({ fields }, planIndex: number) => {
                      const isInPlan =
                        feature.includedInNumberOfPlans <= planIndex + 1;
                      const nextFeatureIsNotInPlan =
                        allFeatures[index + 1]?.includedInNumberOfPlans >
                        planIndex + 1;

                      return (
                        <React.Fragment key={`${index}-${planIndex}`}>
                          <BufferCell />
                          <Checkmark
                            color={
                              isInPlan
                                ? fields?.color || ('white' as CFColorToken)
                                : ('white' as CFColorToken)
                            }
                            lastFeatureInPlan={
                              (isInPlan && nextFeatureIsNotInPlan) ||
                              !allFeatures[index + 1]
                            }
                          >
                            {feature.includedInNumberOfPlans <=
                            planIndex + 1 ? (
                              <PlansIcon
                                name={fields?.name as string}
                                title={feature?.title as string}
                                textColor={fields?.textColor as CFColorToken}
                                replaceWithText={
                                  feature?.replaceCorePlanCheckWith
                                }
                              />
                            ) : (
                              ''
                            )}
                          </Checkmark>
                        </React.Fragment>
                      );
                    })}
                  </FeatureRow>
                ))}
              </tbody>
            </Table>
          </DesktopVersion>
        </BoxColumn>
      </Layout>
    </BlockContainer>
  );
}
